import logging from "utils/logging";
import utils from "./firebaseUtils";
import { collection, getDoc, getDocs, query, where, onSnapshot, addDoc, updateDoc, doc, serverTimestamp } from "firebase/firestore";
import {
  EQUALTO,
  FIREBASE_COLLECTION_GAMES,
  FIREBASE_COLLECTION_QUESTIONS,
  FIREBASE_COLLECTION_SUBMISSIONS,
  FIREBASE_FUNCTION_NAME_COMMIT_QUESTION_SCORES,
  FIREBASE_FUNCTION_NAME_CLOSE_QUESTION_SUBMISSIONS,
  FIREBASE_SUBMISSION_FIELD_PLAYER_ID,
  FIREBASE_FUNCTION_NAME_PREPARE_FINAL,
  FIREBASE_FUNCTION_NAME_COMMIT_FINAL_PLAYER_SCORE,
} from "./constants";
import {
  loadDiffOfModelsForQuerySnapshot,
  loadModelForDocumentSnapshot,
} from "./firebaseModels";
import {
  SUBMISSION_STATE_PENDING,
  SUBMISSION_STATE_WAGER,
} from "features/gamestate/constants";
const log = logging.getLogger("firebase/submission");

export const watchSubmissionsForPlayerQuestion = async (
  { gameId, playerId, questionId },
  onChange
) => {
  log.debug("Watching submissions for: ", gameId, playerId, questionId);

  const submissionCollection = collection(utils.db(), FIREBASE_COLLECTION_GAMES, gameId, FIREBASE_COLLECTION_QUESTIONS, questionId, FIREBASE_COLLECTION_SUBMISSIONS);
  const submissionCheckQuery = query(submissionCollection, where(FIREBASE_SUBMISSION_FIELD_PLAYER_ID, EQUALTO, playerId));

  return onSnapshot(submissionCheckQuery, (qSnap) => {
    let diff = loadDiffOfModelsForQuerySnapshot(qSnap);
    onChange(diff);
  });
};

const watchSubmissionsForQuestion = async (
  { gameId, questionId },
  onChange
) => {
  const submissionsCollection = collection(utils.db(), FIREBASE_COLLECTION_GAMES, gameId, FIREBASE_COLLECTION_QUESTIONS, questionId, FIREBASE_COLLECTION_SUBMISSIONS);
  const submissionsQuery = query(submissionsCollection);
  return onSnapshot(submissionsQuery, (qSnap) => {
    let diff = loadDiffOfModelsForQuerySnapshot(qSnap);
    onChange(diff);
  });
};

const getSubmissions = async (gameId, questionId, playerId) => {
  const submissionsCollection = collection(utils.db(), FIREBASE_COLLECTION_GAMES, gameId, FIREBASE_COLLECTION_QUESTIONS, questionId, FIREBASE_COLLECTION_SUBMISSIONS);
  const submissionsForUserQuery = query(submissionsCollection, where(FIREBASE_SUBMISSION_FIELD_PLAYER_ID, EQUALTO, playerId));

  const submissionQuerySnap = await getDocs(submissionsForUserQuery);

  return loadDiffOfModelsForQuerySnapshot(submissionQuerySnap);
};

export const submitQuestionAction = async (
  gameId,
  questionId,
  playerId,
  answer
) => {
  const submissions = await getSubmissions(gameId, questionId, playerId);

  log.info("Got submissions before submitting: ", submissions);

  let submission = null;

  if (submissions.added.length === 1) {
    submission = submissions.added[0];
    if (submission.answer) {
      return submissions.added[0];
    }

    const submissionRef = doc(utils.db(),
      FIREBASE_COLLECTION_GAMES,
      gameId,
      FIREBASE_COLLECTION_QUESTIONS,
      questionId,
      FIREBASE_COLLECTION_SUBMISSIONS,
      submission.id
    );

    await updateDoc(submissionRef, {
      answer,
      status: SUBMISSION_STATE_PENDING,
      points: 0,
      submittedAt: serverTimestamp(),
    });

    const docSnap = await getDoc(submissionRef);

    submission = loadModelForDocumentSnapshot(docSnap);

    log.info("Updated submission: ", submission);

    return submission;
  } else if (submissions.added.length > 1) {
    log.error("Too many matching subsmissions!");
    return submissions.added[0];
  } else {
    // This is a new submission
    const submissionsRef = collection(utils.db(),
      FIREBASE_COLLECTION_GAMES,
      gameId,
      FIREBASE_COLLECTION_QUESTIONS,
      questionId,
      FIREBASE_COLLECTION_SUBMISSIONS
    );

    let submissionDoc = {
      playerId,
      answer,
      status: SUBMISSION_STATE_PENDING,
      points: 0,
      submittedAt: serverTimestamp(),
    };

    const docRef = await addDoc(submissionsRef, submissionDoc);

    const docSnap = await getDoc(docRef);

    submission = loadModelForDocumentSnapshot(docSnap);

    log.info("Created submission: ", submission);

    return submission;
  }
};

const submitWager = async (gameId, questionId, playerId, wager) => {
  return setWager(gameId, questionId, playerId, wager);
};

/**
 * Upsert a wager for a player. If a submission already exists, updates its
 * wager. Otherwise creates a new submission. Used by both player self-wagering
 * and host-side override from the controller.
 */
const setWager = async (gameId, questionId, playerId, wager) => {
  const submissions = await getSubmissions(gameId, questionId, playerId);

  if (submissions.added.length >= 1) {
    const existing = submissions.added[0];
    const submissionRef = doc(utils.db(),
      FIREBASE_COLLECTION_GAMES,
      gameId,
      FIREBASE_COLLECTION_QUESTIONS,
      questionId,
      FIREBASE_COLLECTION_SUBMISSIONS,
      existing.id
    );
    await updateDoc(submissionRef, { wager });
    const updatedSnap = await getDoc(submissionRef);
    return loadModelForDocumentSnapshot(updatedSnap);
  }

  const submissionsRef = collection(utils.db(),
    FIREBASE_COLLECTION_GAMES,
    gameId,
    FIREBASE_COLLECTION_QUESTIONS,
    questionId,
    FIREBASE_COLLECTION_SUBMISSIONS
  );
  const submissionDoc = {
    playerId,
    wager,
    status: SUBMISSION_STATE_WAGER,
    points: 0,
  };
  const docRef = await addDoc(submissionsRef, submissionDoc);
  const docSnap = await getDoc(docRef);
  return loadModelForDocumentSnapshot(docSnap);
};

const updateSubmission = async (
  gameId,
  questionId,
  submissionId,
  newState,
  newPoints
) => {
  const submissionRef = doc(utils.db(),
    FIREBASE_COLLECTION_GAMES,
    gameId,
    FIREBASE_COLLECTION_QUESTIONS,
    questionId,
    FIREBASE_COLLECTION_SUBMISSIONS,
    submissionId
  );

  if (!newPoints) {
    newPoints = 0;
  }

  await updateDoc(submissionRef, {
    status: newState,
    points: newPoints,
  });
};

const closeQuestionSubmissions = async (gameId, questionId) => {
  const fn = utils.getCallableFunction(
    FIREBASE_FUNCTION_NAME_CLOSE_QUESTION_SUBMISSIONS
  );
  const result = await fn({ gameId, questionId });
  log.debug("Closed wager-only submissions:", result);
  return result.data;
};

const commitSubmissionScoresToLeaderboard = async (gameId, questionId) => {
  const commitScores = utils.getCallableFunction(FIREBASE_FUNCTION_NAME_COMMIT_QUESTION_SCORES);
  const result = await commitScores({ gameId, questionId });
  log.debug("Got result from committing scores:  ", result);
  return result;
};

const prepareFinalReveals = async (gameId) => {
  const prepForFinal = utils.getCallableFunction(FIREBASE_FUNCTION_NAME_PREPARE_FINAL);
  const result = await prepForFinal({ gameId });
  log.debug("Prepared final scores: ", result);
  return result;
};

const commitFinalPlayerScore = async (
  gameId,
  playerId,
  submissionId,
  questionId
) => {
  const commitScore = utils.getCallableFunction(FIREBASE_FUNCTION_NAME_COMMIT_FINAL_PLAYER_SCORE);
  const result = await commitScore({
    gameId,
    playerId,
    questionId,
    submissionId,
  });
  log.debug("Got result from committing score:  ", result);
  return result;
};

const api = {
  submitQuestionAction,
  submitWager,
  setWager,
  watchSubmissionsForPlayerQuestion,
  watchSubmissionsForQuestion,
  updateSubmission,
  commitSubmissionScoresToLeaderboard,
  closeQuestionSubmissions,
  commitFinalPlayerScore,
  prepareFinalReveals,
};

export default api;
